import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Spinner, SpinnerSize, SpinnerStyle, SpinnerWeight} from '@mintdata-wsp/common/components/spinner/Spinner';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {GuideBlock, GuideComment, GuideState} from '@mintdata-wsp/common/state/ui/visualDesigner/GuideState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons} from '@mintdata-wsp/resources/icons/WSPIcons';
import {Shortcuts} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/KeyboardController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import * as classnames from 'classnames/bind';
import {action, computed, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./GuidePanel.less');
const cx = classnames.bind(styles);

interface Props
{
}

interface State
{
}

@observer
export class GuidePanel extends React.Component<Props, State>
{
    constructor(props:any)
    {
        super(props);

        this.state = {};
    }

    componentDidMount():void
    {
    }

    @computed
    private get _stepsCount():number
    {
        const {json} = controllers.ui.guide.getGuideState();

        return json.steps && json.steps.length;
    }

    public render():JSX.Element
    {
        const {json, props: guideProps, isDesignLoading, tour} = controllers.ui.guide.getGuideState();

        if( !json || !guideProps ) return null;

        const {lastOpenedGuidePage} = guideProps;

        if( !lastOpenedGuidePage ) return null;

        const {surfaceArea} = controllers.ui;
        const activePage:Page = surfaceArea.getActivePage();

        if( !activePage ) return null;

        const viewport:Box = activePage.artboard.viewport;

        const top:number = viewport.componentDesc.layoutTop.value.value;
        const left:number = viewport.componentDesc.layoutLeft.value.value;
        const width:number = viewport.componentDesc.layoutWidth.value.value;
        const height:number = viewport.componentDesc.layoutHeight.value.value;

        let panPosition:Point = surfaceArea.getPanPosition();

        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        let _style:React.CSSProperties = {
            top: top * zoom,
            left: left * zoom,
            height,
            transform: `translate3d(${panPosition.x * zoom}px, ${panPosition.y * zoom}px, 0)`,
        };

        let _loaderStyle:React.CSSProperties = {
            width: width * zoom,
            height: height * zoom,
        };

        return (
            <div
                className={cx('wrapper', {isTourVisible: tour.isVisible})}
                style={_style}
                ref={this._onPanelRef}
                onClick={this._onClickWrapper}
            >
                {this._renderWelcomeMessage()}
                {this._renderHeader()}
                {this._renderContent()}
                {this._renderFooter()}

                <div
                    className={cx(styles.loader, {isDesignLoading})}
                    style={_loaderStyle}
                >
                    <Spinner
                        className={cx('spinner')}
                        size={SpinnerSize.Large_64}
                        style={SpinnerStyle.White}
                        weight={SpinnerWeight.Normal}
                    />
                </div>

                {this._renderGuideTourTexts()}
            </div>
        );
    }

    private _renderWelcomeMessage():JSX.Element
    {
        const {props: guideProps} = controllers.ui.guide.getGuideState();

        const {lastOpenedGuidePage} = guideProps;

        if( lastOpenedGuidePage > 1 ) return null;

        const {fullName} = rootState.userSession.currentUser;

        return (
            <div className={styles.welcomeMessage}>

                <div className={styles.hand}>👋</div>

                <div className={styles.text}>
                    <div className={styles.name}>
                        Welcome {fullName}!
                    </div>
                    <div className={styles.follow}>
                        {
                            (this._stepsCount > 0) &&
                            <span>Follow {this._stepsCount} steps to learn MintData</span>
                        }
                        {
                            !this._stepsCount &&
                            <span>Oops! No steps to learn</span>
                        }
                    </div>
                </div>

            </div>
        );
    }

    private _renderHeader():JSX.Element
    {
        if( !this._stepsCount )
            return <div className={styles.header} />;

        const {json, props: guideProps} = controllers.ui.guide.getGuideState();

        const {lastOpenedGuidePage} = guideProps;

        return (
            <div className={cx(styles.header, {notFirstStep: lastOpenedGuidePage > 1})}>
                <div className={styles.dropdown}>
                    {json.steps[lastOpenedGuidePage - 1].title}
                </div>
                <div
                    className={styles.btn}
                    // onClick={this._startOver}
                    onMouseDown={this._startOver}
                >
                    <MDIcon
                        className={styles.btnIconBefore}
                        name={MDIcons.common.Refresh}
                        size={MDIconSize.medium16}
                    />
                    Start over
                </div>
            </div>
        );
    }

    private _startOver = () =>
    {
        const {props: guideProps} = controllers.ui.guide.getGuideState();

        controllers.ui.guide.setDesignLoadingTo(true);

        // need for render loader
        setTimeout(() =>
        {
            controllers.ui.guide.loadDesignForStep(guideProps.lastOpenedGuidePage);
        });
    };

    private _renderContent():JSX.Element
    {
        const {json, props: guideProps} = controllers.ui.guide.getGuideState();

        const {lastOpenedGuidePage} = guideProps;

        if( !this._stepsCount )
            return <div className={styles.contentWrapper} />;

        const blocks:GuideBlock[] = json.steps[lastOpenedGuidePage - 1].blocks || [];

        return (
            <div className={styles.contentWrapper}>
                <div
                    className={styles.content}
                    ref={this._onPanelContentRef}
                >
                    {blocks.map((block:GuideBlock, index:number) => this._renderBlock(block, index))}
                </div>
            </div>
        );
    }

    private _renderBlock(block:GuideBlock, index:number):JSX.Element
    {
        const {description, video, videoPreview, comments} = block;

        return (
            <div
                className={styles.block}
                key={description.substr(0, 20)}
            >
                <div
                    className={styles.descriptionWrapper}
                    // ref={(el:HTMLElement) => this._onBlockRef(el, index)}
                >
                    <div
                        className={styles.description}
                        dangerouslySetInnerHTML={{__html: description}}
                    />
                    {
                        video &&
                        <div
                            className={styles.video}
                            onClick={this._onClickVideo(video)}
                        >
                            {
                                videoPreview &&
                                <img className={styles.videoPreview} src={videoPreview} alt="" />
                            }
                            <div className={styles.playBtn} />
                            <div className={styles.playBtnIcon} />
                        </div>
                    }
                </div>
                {
                    comments &&
                    <div className={styles.comments}>
                        {comments.map((comment:GuideComment) => this._renderComment(comment))}
                    </div>
                }
            </div>
        );
    }

    @action
    private _onPanelRef = (element:HTMLElement) =>
    {
        const {tour} = controllers.ui.guide.getGuideState();

        tour.panelRef = element;
    };

    @action
    private _onPanelContentRef = (element:HTMLElement) =>
    {
        const {tour} = controllers.ui.guide.getGuideState();

        tour.panelContentRef = element;
    };
    
    private _onClickVideo = (videoUrl:string) =>
    {
        return (e:React.MouseEvent<HTMLElement>) =>
        {
            e.preventDefault();

            // wait for hide guideTour with setDotButtonZIndex(null)
            // setTimeout(() =>
            // {
            controllers.ui.lx.setDotButtonZIndex(1);
            // });

            runInAction(() =>
            {
                const {video} = controllers.ui.guide.getGuideState();

                video.isVisible = true;
                video.url = videoUrl;
            });
        };
    };

    // @action
    // private _onBlockRef = (element:HTMLElement, index:number) =>
    // {
    //     const guideState = controllers.ui.guide.getGuideState();
    //
    //     console.log('%c _onBlockRef: element =', 'background:#f60;color:#000;', element);
    //
    //     const {props: {lastOpenedGuidePage}} = guideState;
    //
    //     // only first step -> first block
    //     if( lastOpenedGuidePage !== 1 || index !== 0 ) return;
    //
    //     if( !guideState.tour )
    //     {
    //         guideState.tour = { elementRef: null }
    //     }
    //
    //     guideState.tour.elementRef = element;
    // };

    // @action
    // private _clearBlockRef = () =>
    // {
    //     const guideState = controllers.ui.guide.getGuideState();
    //
    //     guideState.tour.elementRef = null;
    // };

    private _renderComment(comment:GuideComment):JSX.Element
    {
        let {text, tip} = comment;

        if( tip && tip.includes('<kbd>') )
            tip = tip.replace(/(<kbd>)(.*)(<\/kbd>)/g, ($1, $2, $3, $4) =>
            {
                if( !Shortcuts[$3] )
                    return '(TODO: add valid shortcut!)';

                return `${$2}${controllers.ui.keyboard.getShortcutLabel(Shortcuts[$3 as Shortcuts])}${$4}`;
            });

        return (
            <div
                className={styles.comment}
                key={(text || tip).substr(0, 100)}
            >
                {
                    text &&
                    <div
                        className={styles.commentText}
                        dangerouslySetInnerHTML={{__html: text}}
                    />
                }
                {
                    tip &&
                    <div
                        className={styles.commentTip}
                        dangerouslySetInnerHTML={{__html: tip}}
                    />
                }
            </div>
        );
    }

    private _renderFooter():JSX.Element
    {
        if( !this._stepsCount )
            return <div className={styles.footer} />;

        const {props: guideProps} = controllers.ui.guide.getGuideState();

        const {lastOpenedGuidePage} = guideProps;

        return (
            <div className={styles.footer}>
                <div
                    className={cx(styles.btn, {hidden: lastOpenedGuidePage === 1})}
                    // onClick={this._goBack}
                    onMouseDown={this._goBack}
                >
                    Back
                </div>
                <div className={styles.paging}>
                    {lastOpenedGuidePage} of {this._stepsCount}
                </div>
                <div
                    className={cx(styles.btn, styles.blue, {hidden: lastOpenedGuidePage === this._stepsCount})}
                    // onClick={this._goNext}
                    onMouseDown={this._goNext}
                >
                    Next
                    <MDIcon
                        className={styles.btnIconAfter}
                        name={MDIcons.common.ChevronRight}
                        size={MDIconSize.medium16}
                    />
                </div>
            </div>
        );
    }

    private _renderGuideTourTexts():JSX.Element
    {
        const {tour: {isVisible}} = controllers.ui.guide.getGuideState();

        if( !isVisible ) return null;

        return (
            <div className={styles.tourTexts}>
                {this._renderText(styles.tourText1, (
                    // <span>
                    //     Play each video and <br />
                    //     follow the same steps in this design
                    // </span>
                    <img src={`${ConfigUtil.cdnUrl()}/assets/images/guides/first_open_step1_text.svg`}/>
                ))}

                {this._renderText(styles.tourText2, (
                    // <span>
                    //     Click Next to see <br />
                    //     more features
                    // </span>
                    <img src={`${ConfigUtil.cdnUrl()}/assets/images/guides/first_open_step2_text.svg`}/>
                ))}
            </div>
        );
    }

    private _renderText = (className:string, text:string | JSX.Element):JSX.Element =>
    {
        return (
            <div
                className={cx(styles.tourTextWrapper, className)}
            >
                <div className={styles.tourText}>
                    {text}
                </div>
                <WSPIcon
                    className={styles.iconArrow}
                    name={WSPIcons.guide.Arrow}
                />
            </div>
        );
    };

    private _goBack = () =>
    {
        this._goToStep(-1);
    };

    private _goNext = () =>
    {
        this._goToStep(1);
    };

    @action
    private _goToStep = (direction:number) =>
    {
        const guideState:GuideState = controllers.ui.guide.getGuideState();
        const {props: guideProps, tour} = guideState;

        const {lastOpenedGuidePage} = guideProps;

        let nextStep:number = lastOpenedGuidePage + direction;

        if( this._stepsCount && nextStep !== 0 && nextStep <= this._stepsCount )
        {
            guideState.props.lastOpenedGuidePage = nextStep;
            tour.panelContentRef.scrollTop = 0;

            controllers.ui.guide.setDesignLoadingTo(true);

            // if( nextStep !== 1 )
            // {
            //     this._clearBlockRef();
            // }

            // setTimeout -> repaint Guide panel
            // & for render loader
            setTimeout(() =>
            {
                controllers.ui.guide.loadDesignForStep(nextStep);
            });
        }

        this._hideTour();
    };

    private _onClickWrapper = () =>
    {
        this._hideTour();
    };

    @action
    private _hideTour = () =>
    {
        const {tour, video} = controllers.ui.guide.getGuideState();

        tour.isVisible = false;

        if( !video.isVisible )
            controllers.ui.lx.setDotButtonZIndex(null);
    };
}
